import React from 'react'
import { BadgeSkeleton } from 'src/component/ui/badge/badge-skeleton'
import { Skeleton } from 'src/component/ui/skeleton'
import { cn } from 'src/lib/utils'

interface SeriesCardSkeletonProps {
  className?: string
  aspectRatio?: string
  /** Matches active card: border, header row, progress placeholders. */
  variant?: 'default' | 'active'
}

/** Loading placeholder for homepage {@link SeriesCardKeepGoing} (green pill + two-line title hints). */
export const SeriesCardKeepGoingSkeleton = ({
  className,
  aspectRatio = 'aspect-[9/10]',
}: Pick<SeriesCardSkeletonProps, 'className' | 'aspectRatio'>): React.ReactElement => {
  return (
    <div className={cn('relative overflow-hidden rounded-xl border !border-transparent', className)}>
      <Skeleton className={cn('w-full bg-tertiary-grey', aspectRatio)} />
      <div className="absolute inset-0 flex flex-col justify-between p-3 md:p-2 lg:p-3">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-8 w-28 rounded-full bg-primary-gray" />
          <div className="flex flex-1 items-center justify-end gap-2">
            <Skeleton className="h-7 w-24 rounded-full bg-primary-gray" />
            <Skeleton className="h-6 w-6 shrink-0 rounded-full bg-primary-gray" />
          </div>
        </div>
        <div>
          <Skeleton className="mb-1 h-3 w-24 bg-primary-gray" />
          <Skeleton className="mb-3 h-8 w-3/4 max-w-[14rem] bg-primary-gray" />
          <div className="mb-3 flex gap-2">
            <BadgeSkeleton />
            <BadgeSkeleton />
          </div>
          <Skeleton className="mb-2 h-4 w-40 bg-primary-gray" />
          <Skeleton className="h-1.5 w-full rounded-full bg-primary-gray" />
        </div>
      </div>
    </div>
  )
}

export const SeriesCardSkeleton = ({
  className,
  aspectRatio = 'aspect-[6/4]',
  variant = 'default',
}: SeriesCardSkeletonProps): React.ReactElement => {
  if (variant === 'active') {
    return (
      <div className={cn('relative overflow-hidden rounded-xl border !border-transparent', className)}>
        <Skeleton className={cn('w-full bg-tertiary-grey', aspectRatio)} />
        <div className="absolute inset-0 flex flex-col justify-between p-3 md:p-2 lg:p-3">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-8 w-28 rounded-full bg-primary-gray" />
            <div className="flex flex-1 items-center justify-end gap-2">
              <Skeleton className="h-7 w-24 rounded-full bg-primary-gray" />
              <Skeleton className="h-6 w-6 shrink-0 rounded-full bg-primary-gray" />
            </div>
          </div>
          <div>
            <Skeleton className="mb-3 h-8 w-3/4 max-w-[12rem] bg-primary-gray" />
            <div className="mb-3 flex gap-2">
              <BadgeSkeleton />
              <BadgeSkeleton />
            </div>
            <Skeleton className="mb-2 h-4 w-40 bg-primary-gray" />
            <Skeleton className="h-1.5 w-full rounded-full bg-primary-gray" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative overflow-hidden rounded-lg', className)}>
      <Skeleton className={cn('w-full bg-tertiary-grey', aspectRatio)} />
      <div className="absolute right-4 top-4 flex flex-row justify-end">
        <Skeleton className="h-7 w-24 rounded-full bg-primary-gray" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <Skeleton className="mb-3 h-8 w-32 bg-primary-gray" />
        <div className="flex gap-2">
          <BadgeSkeleton />
          <BadgeSkeleton />
          <BadgeSkeleton />
        </div>
      </div>
    </div>
  )
}
